import apiClient from '@/services/api'

export default {
  async createPaymentRequest(_, { associate_id, shift_ids, facility_project_id }) {
    try {
      const { data } = await apiClient.post('/api/payment_requests/create', {
        associate_id,
        shift_ids,
        facility_project_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg || 'The payment request could not be created.')
      }

      return data.id
    } catch (error) {
      throw error instanceof Error ? error : new Error(error?.message || String(error))
    }
  },

  async getPaymentRequestSystems(_, { payment_id }) {
    try {
      const { data } = await apiClient.post(`/api/payment_requests/${payment_id}/systems/get_all`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg || 'Payment systems could not be loaded.')
      }

      return data.systems || []
    } catch (error) {
      throw error instanceof Error ? error : new Error(error?.message || String(error))
    }
  },

  async getPaymentRequestAction(_, { payment_id }) {
    try {
      const { data } = await apiClient.post(`/api/payment_requests/${payment_id}/action/get`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg || 'The payment request status could not be loaded.')
      }

      return data.action
    } catch (error) {
      throw error instanceof Error ? error : new Error(error?.message || String(error))
    }
  },

  async doPaymentRequestAction(_, { payment_id, action, payload = {} }) {
    try {
      const { data } = await apiClient.post(`/api/payment_requests/${payment_id}/do_action/${action}`, payload)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg || 'The payment request could not be updated.')
      }

      return data
    } catch (error) {
      throw error instanceof Error ? error : new Error(error?.message || String(error))
    }
  },

  async getPayments({ rootState }, { page = 1, size = 50, filter = {} }) {
    const manager_scopes = rootState.applicationState.associate.manager_scopes
    try {
      const { data } = await apiClient.post(
        '/api/payment_requests/project/incoming/get_all',
        {
          project_id: rootState.applicationState.currentProject.id,
          page,
          size,
          filter: {
            ...filter,
            history: filter.history && true,
            manager_scopes
          }
        },
        {
          cancelPreviousRequests: true
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the invoices. '}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getOutgoingPayments({ rootState }, { page = 1, size = 50, filter = {} }) {
    const manager_scopes = rootState.applicationState.associate.manager_scopes

    try {
      const { data } = await apiClient.post(
        '/api/payment_requests/project/outgoing/get_all',
        {
          project_id: rootState.applicationState.currentProject.id,
          page,
          size,
          filter: {
            ...filter,
            history: filter.history && true,
            manager_scopes
          }
        },
        {
          cancelPreviousRequests: true
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the invoices. '}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getPaymentsById(_, { ids }) {
    try {
      const { data } = await apiClient.get('/api/payment_requests/project/by_ids', {
        params: {
          ids: ids
        },
        cancelPreviousRequests: true
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the payment requests.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getPayment(_, { payment_id }) {
    try {
      const { data } = await apiClient.post(`api/payment_requests/${payment_id}/get`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the payment request.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getFinalPaycheck(_, { process_id }) {
    try {
      const { data } = await apiClient.get(`/api/final-payment/${process_id}/paycheck`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the final paycheck.'}`)
      }

      return data.result
    } catch (error) {
      throw error.message ?? error
    }
  },

  async createFinalPaycheck(_, { process_id, shift_ids }) {
    try {
      const { data } = await apiClient.post(`/api/final-payment/${process_id}/manual-payment`, {
        shift_ids
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while creating the final paycheck.'}`)
      }

      return data
    } catch (error) {
      throw error.message ?? error
    }
  },

  async completeFinalPaycheck(_, { process_id }) {
    try {
      const { data } = await apiClient.post(`/api/final-payment/${process_id}/complete-without-payment`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while completing the final paycheck.'}`)
      }

      return data
    } catch (error) {
      throw error.message ?? error
    }
  },

  async getPaymentTotalHistory(_, { payment_id }) {
    try {
      const { data } = await apiClient.get(`/api/payment_requests/${payment_id}/total_history`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the adjusted total history.'}`)
      }

      return Array.isArray(data) ? data : (data.total_history ?? data.history ?? data.items ?? data.data ?? [])
    } catch (error) {
      throw error.message
    }
  },

  async manualAdjustPaymentTotal(_, { payment_id, total, comment }) {
    try {
      const { data } = await apiClient.post(`/api/payment_requests/${payment_id}/total/manual`, {
        total,
        comment
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while updating the adjusted total.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async deletePayment(_, { payment_id }) {
    try {
      const { data } = await apiClient.post(`/api/payment_requests/${payment_id}/hard_delete`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while deleting the payment request. '} `)
      }
    } catch (error) {
      throw error.message
    }
  },

  async updatePaymentAttachments(_, { files, payment_id }) {
    try {
      const { data } = await apiClient.post(`/api/payment_request/${payment_id}/files`, {
        file_ids: [...files]
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? "An error occurred while upload payment's attachments."}`)
      }

      return data.files
    } catch (error) {
      throw error.message
    }
  },

  async bulkPaymentRequest(_, { items, action, comment }) {
    try {
      const { data } = await apiClient.post(`api/payment_requests/shift_payments_multiple/${action}`, {
        items,
        comment
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async bulkDeletePaymentRequest(_, { requests_ids }) {
    try {
      const { data } = await apiClient.post('api/payment_requests/bulk_hard_delete', { requests_ids })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async approvePaymentRequest(_, { request_id }) {
    try {
      const { data } = await apiClient.post(`api/payment_requests/${request_id}/set_approve`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.request
    } catch (error) {
      throw error.message
    }
  },

  async approveShiftPayments(_, { request_id, shift_payment_ids, reference_number }) {
    try {
      const { data } = await apiClient.post(`api/payment_requests/${request_id}/shift_payments/approve`, {
        shift_payment_ids,
        reference_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.request
    } catch (error) {
      throw error.message
    }
  },

  async rejectShiftPayments(_, { request_id }) {
    try {
      const { data } = await apiClient.get(`api/payment_requests/${request_id}/shift_payments/reverse`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.request
    } catch (error) {
      throw error.message
    }
  },

  async declineShiftPayments(_, { request_id, shift_payment_ids, comment }) {
    try {
      const { data } = await apiClient.post(`api/payment_requests/${request_id}/shift_payments/decline`, {
        shift_payment_ids,
        comment
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
      return data.request
    } catch (error) {
      throw error.message
    }
  },

  async updateShiftPayments(_, { request_id, shift_payment_ids, comment }) {
    try {
      const { data } = await apiClient.patch(`api/payment_requests/${request_id}/shift_payments/comment`, {
        shift_payment_ids,
        comment
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
      return data
    } catch (error) {
      throw error.message
    }
  },

  async downloadAgreement(_, { payment_id }) {
    try {
      const { data } = await apiClient.get(`/api/payments_requests/${payment_id}/agreement/download`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async sendAgreement(_, { payment_id, email }) {
    try {
      const { data } = await apiClient.get(`/api/payments_requests/${payment_id}/agreement/send_email/${email}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  // Payment Systems

  async getAllPaymentSystems({ commit, rootState }, { project_id } = {}) {
    const currentProjectId = project_id ?? rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/get_project_payment_systems', {
        project_id: currentProjectId
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (rootState.applicationState.currentProject?.id === currentProjectId) {
        commit('SET_PROJECT_SYSTEMS', data.payment_systems)
      }
      return data.payment_systems
    } catch (error) {
      throw error.message
    }
  },

  async enableSystem({ state, rootState, commit }, { payment_system_id }) {
    const currentProjectId = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/enable_payment_system', {
        project_id: currentProjectId,
        payment_system_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      const updatedSystems = state.projectSystems.map(item =>
        item.id === payment_system_id ? data.payment_system : item
      )

      commit('SET_PROJECT_SYSTEMS', updatedSystems)
    } catch (error) {
      throw error.message
    }
  },

  async disableSystem({ state, rootState, commit }, { payment_system_id }) {
    const currentProjectId = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/disable_payment_system', {
        project_id: currentProjectId,
        payment_system_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      const updatedSystems = state.projectSystems.map(item =>
        item.id === payment_system_id ? data.payment_system : item
      )

      commit('SET_PROJECT_SYSTEMS', updatedSystems)
    } catch (error) {
      throw error.message
    }
  },

  async getSystemStatistic(_, { payment_system_id }) {
    try {
      const { data } = await apiClient.post(`/api/payment_systems/${payment_system_id}/statistic/get`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      return data.statistics
    } catch (error) {
      throw error.message
    }
  },

  async getSystemTemplates({ commit }) {
    try {
      const { data } = await apiClient.post('/api/get_payment_system_templates')

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      commit('SET_SYSTEM_TEMPLATES', data.templates)
    } catch (error) {
      throw error.message
    }
  },

  async updateSystem({ state, commit }, { id, project_ids, fields }) {
    try {
      const { data } = await apiClient.post('/api/update_payment_system', {
        id,
        project_ids,
        fields
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      const updatedSystems = state.projectSystems.map(item => (item.id === id ? data.payment_system : item))

      commit('SET_PROJECT_SYSTEMS', updatedSystems)
    } catch (error) {
      throw error.message
    }
  },

  async createSystem({ rootState, state, commit }, { template_id, project_ids, fields }) {
    const currentProjectId = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/create_payment_system', {
        template_id,
        owner_project_id: currentProjectId,
        project_ids,
        fields
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      commit('SET_PROJECT_SYSTEMS', [...state.projectSystems, data.payment_system])
    } catch (error) {
      throw error.message
    }
  }
}
